import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ModalController, IonicModule } from '@ionic/angular';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  startWith,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppData } from 'src/app/data';
import {
  BaseJob,
  JobPerform,
  JobType,
  NoShowOptions,
  NoShowReasonEnum,
  PassTypeEnum,
  Job,
  Pickup,
  createPaymentInfo,
  PaymentInfo,
  FareTypeEnum,
  FareType,
} from 'src/app/types';
import {
  calculateAdditionalFare,
  calculateRiderFare,
  formatCurrency,
  getTranslatedNoShowOptionList,
} from 'src/app/helpers/ui-helpers';

import {
  ErrorHandlerService,
  RouteProviderService,
  TranslateService,
  LocationService,
  PassResults,
  NotificationService,
  AlertService,
} from 'src/app/services';
import { PaymentsComponent } from 'src/app/components/payments/payments.component';
import { AddressComponent } from '../../components/address/address.component';
import { NgClass, CurrencyPipe, DatePipe } from '@angular/common';
import { IncrementalControlComponent } from '../../components/increment-control/increment-control.component';
import { FormErrorComponent } from '../../components/form-error/form-error.component';
import { CurrencyComponent } from '../../components/currency/currency.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  templateUrl: './job-perform.dialog.html',
  styleUrls: ['./job-perform.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonicModule,
    AddressComponent,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    IncrementalControlComponent,
    FormErrorComponent,
    CurrencyComponent,
    CurrencyPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class JobPerformDialog implements OnDestroy, OnInit {
  private destroyed$ = new Subject<boolean>();
  private readonly gtm = (<any>window).dataLayer || [];
  private readonly modalCtrl = inject(ModalController);
  public readonly data = inject(AppData);
  private readonly routeProvider = inject(RouteProviderService);
  private readonly translateSvc = inject(TranslateService);
  private readonly locationSvc = inject(LocationService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly notificationSvc = inject(NotificationService);
  private readonly alertService = inject(AlertService);

  public readonly paraPassEnabled = this.data.paraPassEnabled;
  public readonly features = environment.features;
  public readonly PassTypeEnum = PassTypeEnum;
  public readonly FareTypeEnum = FareTypeEnum;
  public noShowOptions: NoShowOptions[];

  public jobEvent: Job;
  public job: BaseJob;
  public jobTypes = JobType;
  public isPickup: boolean;
  public perform: JobPerform;
  public ridePaymentInfo: PaymentInfo;
  public submitting = false;
  public readonly translationKeys = environment.translationKeys;
  public remainingBalance: number;
  public balanceWarning: number = this.data.route.balanceWarning ?? Infinity;
  public showBalance = false;
  public infinity = Infinity;
  public addToBalanceEnabled = !!this.data.route.creditCardDataTokenizationKey;
  public overpaymentEnabled = !!this.data.route.overpaymentToBalanceEnabled;
  public handlingCCPayment = false;
  public invalidFareType = false;
  public invalidAdditionalFareType = false;

  public form = new FormGroup({
    signature: new FormControl<string>(null),
    signatureReason: new FormControl<string>(null),
    noShowReason: new FormControl<NoShowReasonEnum>(null),
    passResults: new FormControl<PassResults>(null),
    odometer: new FormControl<number>(this.data.gpsReport.odometer, [
      Validators.required,
      Validators.max(999999),
      Validators.min(0),
    ]),
    fareTypeId: new FormControl<number>(null),
    fareTypeOptions: new FormControl<number>(null),
    fareCollected: new FormControl<number>(0, [
      Validators.required,
      Validators.max(999999),
      Validators.min(0),
    ]),
    numberOfEscorts: new FormControl<number>(
      { value: 0, disabled: !this.features.editPassengerCount },
      [Validators.min(0), Validators.max(100)],
    ),
    numberOfChildren: new FormControl<number>(0, [Validators.min(0), Validators.max(100)]),
    units: new FormControl<number>(0, [Validators.min(0), Validators.max(100)]),
    additionalFareTypeId: new FormControl<number>(null),
    additionalRideCount: new FormControl<number>(0),
    driverNotes: new FormControl<string>(null, [Validators.maxLength(10_000)]),
  });
  public isNoShow = this.form.value.noShowReason !== null;
  public canCollectFare: boolean;

  public initialValue: BaseJob | null = null;
  get _getAdditionalRideFare() {
    return this.features.additionalRideFare
      ? (this.form.controls.numberOfChildren.value + this.form.controls.numberOfEscorts.value) * (this.initialValue?.additionalRideFare ?? 0)
      : 0;
    }
  /** for features.additionalRideFare, stores the additional rider's portion of the fare, default it to 0 if we don't have additionalRideFare enabled */
  public additionalRideFare: number = this._getAdditionalRideFare;
  /** for features.additionalRideFare, stores the rider's portion of the fare */
  public rideFare: number = (this.initialValue?.rideFare ?? 0) - this.additionalRideFare;

  private _disconnected$ = this.data.connectionStatus$.pipe(
    map(status => !status.connected || status?.connectionType === 'none'),
    tap(connected => (this.disconnected = connected)),
  );
  public disconnected: boolean;

  set props({
    routeEvent,
    jobPerform,
    noShow,
  }: {
    routeEvent: Pickup
    jobPerform: JobPerform
    noShow: boolean
  }) {
    this.jobEvent = routeEvent;
    this.job = routeEvent.value;
    this.perform = jobPerform;

    if (!this.initialValue) {
      this.initialValue = structuredClone(this.job);
      this.additionalRideFare = this._getAdditionalRideFare;
      this.rideFare = (this.initialValue?.rideFare ?? 0) - this.additionalRideFare;
    }

    this.ridePaymentInfo = createPaymentInfo(this.job.ridePaymentIsSuccessful);
    this.isPickup = routeEvent.value.jobType === JobType.Pickup;
    this.canCollectFare =
      !this.ridePaymentInfo?.hasPayment ||
      (this.ridePaymentInfo.hasPayment && !this.ridePaymentInfo.isSuccessful);
    this.noShowOptions = getTranslatedNoShowOptionList(
      routeEvent.value,
      this.routeProvider,
      this.translateSvc,
    );

    this.form.controls.additionalRideCount.patchValue(this.job.additionalRideCount);
    if (!this.features.editPassengerCount) {
      this.form.controls.additionalRideCount.disable();
    } else {
      this.form.controls.additionalRideCount.enable();
    }

    this.form.patchValue({ ...jobPerform, noShowReason: jobPerform.noShowReason ?? null });
    this.form.markAllAsTouched();
    const { fareTypeId: fareTypeId, additionalFareTypeId: additionalFareTypeId } = jobPerform;
    this.handleRemainingBalance({ fareTypeId, additionalFareTypeId });

    //  handle No Show
    this.isNoShow = !!noShow;
    const noShowReason = noShow ? this.noShowOptions[0]?.id || 0 : null;
    this.form.controls.noShowReason.patchValue(noShowReason);
    if (this.data.paraPassEnabled && this.job.ePassId && this.isPickup && !this.isNoShow) {
      this.handlePass(this.job.ePassId);
    }
  }

  constructor() {
    combineLatest([
      this.form.controls.fareTypeId.valueChanges.pipe(
        distinctUntilChanged(),
        startWith(this.form.controls.fareTypeId.value),
      ),
      this.form.controls.additionalFareTypeId.valueChanges.pipe(
        distinctUntilChanged(),
        startWith(this.form.controls.additionalFareTypeId.value),
      ),
    ])
      .pipe(
        tap(([fareTypeId, additionalFareTypeId]) => {
          if (!environment.features.fareShowType) {
            this.form.controls.fareCollected.enable();
          } else {
            if (
              !this.isFareTypeNormal() &&
              (!this.isAdditionalFareTypeNormal() || this.job?.additionalRideCount < 1)
            ) {
              this.form.controls?.fareCollected?.disable();
            } else {
              this.form.controls?.fareCollected?.enable();
            }
            this.form.controls.fareCollected.patchValue(this.expectedFareCollected);
            this.handleRemainingBalance({ fareTypeId, additionalFareTypeId });
          }
          if (!environment.features.fareShowType || this.fareTypeExists(fareTypeId)) {
            this.invalidFareType = false;
          }
          if (
            !environment.features.fareAdditionalPassengers ||
            this.fareTypeExists(additionalFareTypeId)
          ) {
            this.invalidAdditionalFareType = false;
          }
        }),
        takeUntil(this.destroyed$),
        catchError(async err => {
          console.error(err);
        }),
      )
      .subscribe();
    this._disconnected$.pipe(takeUntil(this.destroyed$)).subscribe();

    if (this.features.additionalRideFare) {
      //  Listen to changes in the form for to recalculate the additional rider's portion of the fare
      this.form.valueChanges.pipe(
        tap(() => {
          this.additionalRideFare = this._getAdditionalRideFare;
        }),
        takeUntil(this.destroyed$),
      ).subscribe();
    }
  }

  ngOnInit(): void {
    if (this.isFareTypeBilled(this.job?.defaultFareTypeId)) {
      this.form.controls?.fareTypeId.disable();
    }
    if (this.isFareTypeBilled(this.job?.additionalDefaultFareTypeId)) {
      this.form.controls?.additionalFareTypeId.disable();
    }
    if (!this.fareTypeExists(this.job?.defaultFareTypeId)) {
      this.invalidFareType = true;
    }
    if (
      this.job?.additionalRideCount > 0 &&
      !this.fareTypeExists(this.job?.additionalDefaultFareTypeId)
    ) {
      this.invalidAdditionalFareType = true;
    }
  }

  public back(): void {
    this.modalCtrl.dismiss(null, 'cancel', 'jobPerformDialog');
  }

  public async handlePass(ePassId: string = null): Promise<void> {
    if (!ePassId) {
      this.gtm.push({ event: 'custom_event', custom_event_name: 'click_ scan_pass' });
    }
    const passResults = await this.jobEvent.scanPass(ePassId);
    if (passResults?.denied) {
      this.form.controls.noShowReason.patchValue(NoShowReasonEnum.DeniedByDriver);
      await this.onSubmit();
      return;
    }
    if (passResults) {
      const _results = { passResults };
      this.form.patchValue({ passResults });
    }
  }

  public removePass(): void {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'click_ remove_pass' });
    const { passResults } = this.form.controls;
    passResults.patchValue(null);
  }

  public getCurrency(value: number): string {
    return formatCurrency(value);
  }

  async payWithCreditCard() {
    if (!this.data.route.creditCardDataTokenizationKey) {
      return;
    }
    this.handlingCCPayment = true;
    try {
      const performDialog = await this.modalCtrl.create({
        cssClass: 'parascope-full-modal',
        component: PaymentsComponent,
        componentProps: { routeEvent: this.jobEvent, jobPerform: this.perform, baseJob: this.job },
      });
      await performDialog.present();
      const dialog = performDialog.shadowRoot.querySelector('[role="dialog"]');
      dialog.setAttribute('aria-label', 'Payments');
      const res = await performDialog.onDidDismiss();
      const { currentBalance } = res?.data || {};
      if (!currentBalance) {
        return;
      }
      this.job.riderAccountBalance = currentBalance;
      const { fareTypeId, additionalFareTypeId } = this.form.getRawValue();
      this.handleRemainingBalance({ fareTypeId, additionalFareTypeId });
    } catch (err) {
      console.error(err);
      this.notificationSvc.error('MESSAGE.genericError');
    } finally {
      this.handlingCCPayment = false;
    }
  }

  public async captureSignature(): Promise<void> {
    const res = await this.jobEvent.captureSignature();
    if (res.role === 'cancel') {
      return;
    }
    const { signature, signatureReason } = res.data || {};
    this.form.controls.signature.patchValue(signature);
    this.form.controls.signatureReason.patchValue(signatureReason);
  }

  private handleRemainingBalance({ fareTypeId, additionalFareTypeId }) {
    const fareType = this.data.fareTypes.find(type => type.id === fareTypeId);
    const additionalFareType = this.data.fareTypes.find(type => type.id === additionalFareTypeId);
    const riderAccountBalance = this.job?.riderAccountBalance ?? Infinity;
    const rideFare = calculateRiderFare(this.job) ?? 0;
    const additionalFare = calculateAdditionalFare(this.job) ?? 0;

    if (
      fareType?.type === FareTypeEnum.BALANCE &&
      additionalFareType?.type === FareTypeEnum.BALANCE
    ) {
      this.remainingBalance = riderAccountBalance - (rideFare + additionalFare);
      this.showBalance = this.remainingBalance < Infinity;
    } else if (fareType?.type === FareTypeEnum.BALANCE) {
      this.remainingBalance = riderAccountBalance - rideFare;
      this.showBalance = this.remainingBalance < Infinity;
    } else if (additionalFareType?.type === FareTypeEnum.BALANCE) {
      this.remainingBalance = riderAccountBalance - additionalFare;
      this.showBalance = this.remainingBalance < Infinity;
    } else {
      this.remainingBalance = this.data.fareTypes.some(t => t.type === FareTypeEnum.BALANCE)
        ? (this.job?.riderAccountBalance ?? Infinity)
        : Infinity;
      this.showBalance = false;
    }
  }

  public async confirmPaymentMismatch(payload: JobPerform): Promise<boolean> {
    const isUnderpaying = this.expectedFareCollected > payload.fareCollected;
    const isOverpaying = this.expectedFareCollected < payload.fareCollected;

    if (!this.canCollectFare || (!isUnderpaying && !isOverpaying)) {
      return true;
    }

    if (isUnderpaying) {
      return this.showUnderpaymentAlert();
    }

    if (isOverpaying && !this.overpaymentEnabled) {
      return this.showOverpaymentAlert();
    }

    return true;
  }

  private async showUnderpaymentAlert(): Promise<boolean> {
    return new Promise<boolean>(async resolve => {
      await this.alertService.presentAlertAsync({
        header: this.translateSvc.translate('UNDERPAYMENT.title'),
        message: this.translateSvc.translate('UNDERPAYMENT.message'),
        buttons: [
          {
            text: this.translateSvc.translate('LABEL.continue'),
            cssClass: 'primary',
            handler: async () => resolve(true),
          },
          {
            text: this.translateSvc.translate('ACTION.cancel'),
            cssClass: 'danger',
            handler: () => resolve(false),
          },
        ],
      });
    });
  }

  private async showOverpaymentAlert(): Promise<boolean> {
    return new Promise<boolean>(async resolve => {
      await this.alertService.presentAlertAsync({
        header: this.translateSvc.translate('OVERPAYMENT.title'),
        message: this.translateSvc.translate('OVERPAYMENT.message'),
        buttons: [
          {
            text: this.translateSvc.translate('LABEL.continue'),
            cssClass: 'primary',
            handler: async () => resolve(true),
          },
          {
            text: this.translateSvc.translate('ACTION.cancel'),
            cssClass: 'danger',
            handler: () => resolve(false),
          },
        ],
      });
    });
  }

  disableSubmit(): boolean {
    return (
      this.submitting ||
      this.form.invalid ||
      (this.isPickup &&
        ((environment.features.fareShowType && this.invalidFareType) ||
          (environment.features.fareAdditionalPassengers && this.invalidAdditionalFareType)))
    );
  }

  fareTypeExists(fareTypeId: number): boolean {
    return this.data.fareTypes.some(type => type.id === fareTypeId);
  }

  isFareTypeNormal(): boolean {
    const fareTypeId = this.form.controls.fareTypeId.value;
    const fareType = this.data.fareTypes.find(type => type.id === fareTypeId);
    return fareType?.type === FareTypeEnum.NORMAL;
  }

  isAdditionalFareTypeNormal(): boolean {
    const additionalFareTypeId = this.form.controls.additionalFareTypeId.value;
    const additionalFareType = this.data.fareTypes.find(type => type.id === additionalFareTypeId);
    return additionalFareType?.type === FareTypeEnum.NORMAL;
  }

  isFareTypeBilled(fareTypeId: number): boolean {
    const fareType = this.data.fareTypes.find(type => type.id === fareTypeId);
    return fareType?.type === FareTypeEnum.BILLED;
  }

  isFareTypeExternal(): boolean {
    const fareTypeId = this.form.controls.fareTypeId.value;
    const fareType = this.data.fareTypes.find(type => type.id === fareTypeId);
    return fareType?.type === FareTypeEnum.EXTERNAL;
  }

  isAdditinalFareTypeExternal(): boolean {
    const fareTypeId = this.form.controls.additionalFareTypeId.value;
    const fareType = this.data.fareTypes.find(type => type.id === fareTypeId);
    return fareType?.type === FareTypeEnum.EXTERNAL;
  }

  calculateOverage(): number {
    const fareCollected = this.form.controls.fareCollected.value;
    return fareCollected - this.expectedFareCollected;
  }

  get expectedFareCollected(): number {
    //  CTS passes
    const passResults = this.form.controls.passResults.value;
    let expectedFareCollected = 0;

    if (
      //  has Flex Pass
      passResults?.passInformation?.passType === this.PassTypeEnum.Flex ||
      //  has pass but we don't know what kind (offline scenario)
      (passResults && !passResults.passInformation)
    ) {
      expectedFareCollected = this.additionalRideFare;

      //  Ecolane Fare System
    } else if (environment.features.fareShowType) {
      const rideFare = this.isFareTypeNormal() ? calculateRiderFare(this.job) : 0;
      const additionalFare = this.isAdditionalFareTypeNormal()
        ? calculateAdditionalFare(this.job)
        : 0;
      expectedFareCollected = rideFare + additionalFare;

      //  CTS Fare, no pass or cash pass
      } else {
        const valueProvidedByPass = (passResults?.fareCollected ?? 0) <= 0 ? 0 : passResults?.fareCollected;
        expectedFareCollected = this.initialValue.rideFare + this.additionalRideFare - valueProvidedByPass;
      }

      const fareCollectedControl = this.form.controls.fareCollected;
      if (!fareCollectedControl.dirty && this.features.syncFare) { fareCollectedControl.patchValue(expectedFareCollected); }

      return expectedFareCollected;
    }

    public async onSubmit(): Promise<void> {
    try {
      this.submitting = true;
      const { passResults, odometer, ...props } = this.form.getRawValue();
      await this.locationSvc.setOdometer(odometer);
      let payload: JobPerform;
      if (this.isPickup) {
        payload = {
          ...this.perform,
          ...props,
          passId: props.noShowReason === null ? passResults?.passId || null : null,
          noShowReasonText: props.noShowReason !== null ? this.noShowOptions.find(o => o.id === props.noShowReason)?.displayValue : null,
          rideFareHandleType: this.data.fareTypes.find(type => type.id === props.fareTypeId)?.type,
          additionalRideFareHandleType: this.data.fareTypes.find(type => type.id === props.additionalFareTypeId)?.type,
        };
      } else {
        const { signature, signatureReason, driverNotes } = this.form.value;
        payload = { ...this.perform, signature, signatureReason, driverNotes };
      }

      const confirmed = this.isPickup && props.noShowReason === null ? await this.confirmPaymentMismatch(payload) : true;
      if (!confirmed) {
        this.submitting = false;
        return;
      }

      await this.routeProvider.performJob(payload);
      await this.modalCtrl.dismiss(payload, 'submit', 'jobPerformDialog');
    } catch(error) {
      this.errorHandler.handleError(error, false);
    } finally {
      this.submitting = false;
    }
  }

  public showFareOption(option: FareType): boolean {
    return !!this.job.externalAccount?.id || option.type !== FareTypeEnum.EXTERNAL;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}